import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  joinSession as joinSupabaseSession,
  submitVote as submitSupabaseVote,
  getSessionData,
  subscribeToSession,
  unsubscribeFromSession,
  getClientId
} from '../lib/planningPoker'
import LanguageSwitcher from '../components/LanguageSwitcher'
import DarkModeToggle from '../components/DarkModeToggle'
import SkipNavigation from '../components/SkipNavigation'

const votingOptions = ['0', '1', '2', '3', '5', '8', '13', '21', '34', '55', '?', '☕']

// Helper to get accessible label for vote option
const getVoteLabel = (option, t) => {
  if (option === '?') return t('accessibility.voteUnknown', 'Unknown or uncertain')
  if (option === '☕') return t('accessibility.voteBreak', 'Take a break')
  return t('accessibility.votePoints', { points: option, defaultValue: `${option} points` })
}

function Player() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const sessionCode = searchParams.get('code')
  const playerName = searchParams.get('name')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentTopic, setCurrentTopic] = useState('')
  const [currentIssue, setCurrentIssue] = useState(null)
  const [currentVote, setCurrentVote] = useState(null)
  const [players, setPlayers] = useState([])
  const [totalPlayers, setTotalPlayers] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const [rounds, setRounds] = useState([])
  const [channel, setChannel] = useState(null)
  const [joined, setJoined] = useState(false)
  const [liveAnnouncement, setLiveAnnouncement] = useState('')
  const [focusedVoteIndex, setFocusedVoteIndex] = useState(0)
  const voteButtonRefs = useRef([])

  // Join session once
  useEffect(() => {
    if (!sessionCode || !playerName) {
      setError(t('player.invalidParams'))
      setLoading(false)
      return
    }

    if (joined) return

    const joinSession = async () => {
      const response = await joinSupabaseSession(sessionCode, playerName)
      if (response.success) {
        setLoading(false)
        setJoined(true)
        updateSessionData(response.session)
      } else {
        setError(response.error || t('player.sessionNotFound'))
        setLoading(false)
      }
    }

    joinSession()
  }, [sessionCode, playerName, joined, t])

  // Subscribe to realtime updates (separate effect)
  useEffect(() => {
    if (!joined || !sessionCode) return

    console.log('[Player] Setting up Realtime subscription')

    const newChannel = subscribeToSession(sessionCode, async (update) => {
      console.log('[Player] Received update:', update)
      // Fetch fresh session data whenever there's a change
      const sessionData = await getSessionData(sessionCode, false)
      if (sessionData) {
        updateSessionData(sessionData)

        // Update current vote based on our client ID
        const clientId = getClientId()
        const myPlayer = sessionData.players.find((p) => p.id === clientId)
        if (myPlayer && myPlayer.vote && myPlayer.vote !== '✓') {
          setCurrentVote(myPlayer.vote)
        } else if (sessionData.revealed === false && myPlayer && !myPlayer.vote) {
          setCurrentVote(null)
        }
      }
    })

    setChannel(newChannel)

    // Initial data fetch after subscribing
    getSessionData(sessionCode, false).then((sessionData) => {
      if (sessionData) {
        updateSessionData(sessionData)

        // Set initial vote
        const clientId = getClientId()
        const myPlayer = sessionData.players.find((p) => p.id === clientId)
        if (myPlayer && myPlayer.vote && myPlayer.vote !== '✓') {
          setCurrentVote(myPlayer.vote)
        } else if (sessionData.revealed === false && myPlayer && !myPlayer.vote) {
          setCurrentVote(null)
        }
      }
    })

    return () => {
      console.log('[Player] Cleaning up Realtime subscription')
      unsubscribeFromSession(newChannel)
    }
  }, [joined, sessionCode])

  const updateSessionData = (data) => {
    const playersList = data.players || []
    const previousPlayerCount = totalPlayers
    const wasRevealed = revealed

    setPlayers(playersList)
    setTotalPlayers(playersList.length)
    setRounds(data.rounds || [])
    setCurrentTopic(data.topic || '')
    setCurrentIssue(data.issue || null)

    // Announce changes to screen readers
    if (data.topic && data.topic !== currentTopic) {
      setLiveAnnouncement(t('accessibility.topicChanged', { topic: data.topic, defaultValue: `Topic changed to: ${data.topic}` }))
    }

    if (playersList.length > previousPlayerCount) {
      setLiveAnnouncement(t('accessibility.playerJoined', { count: playersList.length, defaultValue: `Player joined. Total players: ${playersList.length}` }))
    }

    if (data.revealed !== undefined) {
      if (data.revealed && !wasRevealed) {
        const votedCount = playersList.filter(p => p.vote !== null).length
        setLiveAnnouncement(t('accessibility.votesRevealed', { count: votedCount, defaultValue: `Votes revealed. ${votedCount} players voted.` }))
      } else if (!data.revealed && wasRevealed) {
        setLiveAnnouncement(t('accessibility.newRound', 'New round started. You can vote again.'))
      }
      setRevealed(data.revealed)
    }
  }

  const handleVote = async (vote) => {
    if (revealed) return

    setCurrentVote(vote)
    await submitSupabaseVote(sessionCode, vote)
    setLiveAnnouncement(t('accessibility.voteSubmitted', { vote: getVoteLabel(vote, t), defaultValue: `Vote submitted: ${vote}` }))
  }

  const handleKeyboardNavigation = (e) => {
    if (revealed || !currentTopic) return

    const key = e.key
    let newIndex = focusedVoteIndex

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      e.preventDefault()
      newIndex = (focusedVoteIndex + 1) % votingOptions.length
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      e.preventDefault()
      newIndex = (focusedVoteIndex - 1 + votingOptions.length) % votingOptions.length
    } else if (key === 'Enter' || key === ' ') {
      e.preventDefault()
      handleVote(votingOptions[focusedVoteIndex])
      return
    } else if (key === 'Home') {
      e.preventDefault()
      newIndex = 0
    } else if (key === 'End') {
      e.preventDefault()
      newIndex = votingOptions.length - 1
    }

    if (newIndex !== focusedVoteIndex) {
      setFocusedVoteIndex(newIndex)
      voteButtonRefs.current[newIndex]?.focus()
    }
  }

  const getVoteGroups = () => {
    const groups = {}
    players.forEach(player => {
      if (player.vote !== null) {
        if (!groups[player.vote]) {
          groups[player.vote] = []
        }
        groups[player.vote].push(player.name)
      }
    })
    return Object.entries(groups).sort((a, b) => {
      const aNum = parseFloat(a[0])
      const bNum = parseFloat(b[0])
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
      return a[0].localeCompare(b[0])
    })
  }

  const voteGroups = revealed ? getVoteGroups() : []

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
          <DarkModeToggle />
          <LanguageSwitcher />
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center transition-colors">
          <p className="text-lg dark:text-white">{t('player.connectingToSession')}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
          <DarkModeToggle />
          <LanguageSwitcher />
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full transition-colors">
          <h2 className="text-2xl font-bold mb-4 text-red-600 dark:text-red-400">❌ {t('common.error')}</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-lg p-3 font-semibold transition-all"
          >
            {t('player.backToHome')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <SkipNavigation />
      {/* Live region for screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {liveAnnouncement}
      </div>
      <div className="min-h-screen p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-white">
              <span aria-hidden="true">🎮 </span>
              {t('player.title')}
            </h1>
            <div className="flex items-center gap-3" role="navigation" aria-label={t('accessibility.utilities', 'Utilities')}>
            <DarkModeToggle />
            <LanguageSwitcher />
            <div className="bg-white/20 dark:bg-gray-800/50 text-white px-4 py-2 rounded-lg font-semibold transition-colors">
              {t('common.session')}: {sessionCode}
            </div>
          </div>
        </div>

        {/* Topic & Issue */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mb-6 transition-colors" id="main-content">
          <h2 className="text-lg font-bold mb-3 dark:text-white">{t('player.currentTopic')}</h2>
          <div
            className="bg-indigo-50 dark:bg-indigo-900/30 border-l-4 border-indigo-500 dark:border-indigo-400 p-4 rounded min-h-[60px] flex items-center transition-colors"
            role="status"
            aria-live="polite"
          >
            {currentTopic ? (
              <p className="font-semibold text-indigo-900 dark:text-indigo-100">{currentTopic}</p>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 italic">{t('player.waitingForTopic')}</p>
            )}
          </div>

          {/* Linked Issue */}
          {currentIssue && (
            <div className="mt-3 bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-400 p-3 rounded transition-colors">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">🔗 {t('player.linkedIssue')}:</span>
                <span className="px-2 py-0.5 text-xs font-semibold rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                  {currentIssue.platform === 'github' ? 'GitHub' : 'GitLab'}
                </span>
                {currentIssue.number && (
                  <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
                    {currentIssue.number}
                  </span>
                )}
              </div>
              {currentIssue.title && (
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {currentIssue.title}
                </p>
              )}
              <a
                href={currentIssue.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-green-700 dark:text-green-400 hover:underline break-all"
              >
                {currentIssue.url}
              </a>
            </div>
          )}
        </div>

        {/* Voting Cards */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mb-6 transition-colors">
          <h2 className="text-lg font-bold mb-4 dark:text-white">{t('player.chooseCard')}</h2>
          <div
            className="grid grid-cols-4 md:grid-cols-6 gap-3 mb-4"
            role="group"
            aria-label={t('accessibility.votingCards', 'Voting cards')}
            onKeyDown={handleKeyboardNavigation}
          >
            {votingOptions.map((option, index) => (
              <button
                key={option}
                ref={(el) => (voteButtonRefs.current[index] = el)}
                onClick={() => handleVote(option)}
                disabled={revealed || !currentTopic}
                tabIndex={focusedVoteIndex === index ? 0 : -1}
                aria-label={getVoteLabel(option, t)}
                aria-pressed={currentVote === option}
                className={`aspect-[2/3] rounded-lg border-4 text-2xl md:text-3xl font-bold transition-all transform ${
                  currentVote === option
                    ? 'bg-indigo-600 dark:bg-indigo-500 text-white border-indigo-600 dark:border-indigo-500 scale-105'
                    : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400 hover:-translate-y-1'
                } ${(revealed || !currentTopic) ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'}`}
              >
                <span aria-hidden="true">{option}</span>
              </button>
            ))}
          </div>

          {!currentTopic && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-500 dark:border-amber-400 rounded-lg p-4 text-center mb-4 transition-colors">
              <p className="text-amber-700 dark:text-amber-300 font-semibold">
                🔒 {t('player.waitingForTopic')}
              </p>
            </div>
          )}

          {currentVote && (
            <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-500 dark:border-green-400 rounded-lg p-4 text-center transition-colors">
              <p className="text-green-700 dark:text-green-300 font-semibold">
                ✓ {t('player.yourVote')}: <strong className="text-xl">{currentVote}</strong>
              </p>
            </div>
          )}
        </div>

        {/* Players Status */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mb-6 transition-colors">
          <h2 className="text-lg font-bold mb-4 dark:text-white">
            {t('common.players')} ({totalPlayers})
          </h2>
          <div className="space-y-2" role="list" aria-label={t('accessibility.playersList', 'Players list')}>
            {players.map((player, idx) => {
              const isMe = player.name === playerName
              const voteStatus = player.vote !== null ? (revealed ? player.vote : '✓') : '⏳'

              const statusLabel = player.vote !== null
                ? (revealed ? t('accessibility.playerVoted', { name: player.name, vote: player.vote, defaultValue: `${player.name} voted ${player.vote}` }) : t('accessibility.playerHasVoted', { name: player.name, defaultValue: `${player.name} has voted` }))
                : t('accessibility.playerWaiting', { name: player.name, defaultValue: `${player.name} is waiting to vote` })

              return (
                <div
                  key={idx}
                  role="listitem"
                  className={`flex justify-between items-center p-3 rounded-lg border-2 transition-all ${
                    isMe
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-400'
                      : player.vote !== null
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400'
                      : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                  }`}
                  aria-label={statusLabel}
                >
                  <span className={`font-semibold ${isMe ? 'text-indigo-900 dark:text-indigo-100' : 'dark:text-white'}`}>
                    {player.name} {isMe ? `(${t('player.you')})` : ''}
                  </span>
                  <span className="text-xl dark:text-gray-300" aria-hidden="true">{voteStatus}</span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Results */}
        {revealed && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 transition-colors" role="region" aria-label={t('accessibility.votingResults', 'Voting results')}>
            <h2 className="text-xl font-bold mb-4 dark:text-white">
              <span aria-hidden="true">🎉 </span>
              {t('player.resultsTitle')}
            </h2>
            <div className="space-y-3">
              {voteGroups.map(([vote, names]) => (
                <div key={vote} className="bg-gray-50 dark:bg-gray-700 border-l-4 border-indigo-500 dark:border-indigo-400 p-4 rounded transition-colors">
                  <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">{vote}</div>
                  <div className="flex flex-wrap gap-2">
                    {names.map((name, idx) => (
                      <span key={idx} className="bg-white dark:bg-gray-600 px-3 py-1 rounded-full text-sm border border-gray-300 dark:border-gray-500 dark:text-gray-200">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Round History */}
        {rounds.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mt-6 transition-colors" role="region" aria-label={t('accessibility.roundHistory', 'Round history')}>
            <h2 className="text-xl font-bold mb-4 dark:text-white">
              <span aria-hidden="true">📋 </span>
              {t('player.roundHistory')}
            </h2>
            <div className="space-y-3">
              {rounds.map((round, idx) => (
                <div key={round.id || idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h4 className="font-bold dark:text-white">
                        {t('player.round')} {rounds.length - idx}
                      </h4>
                      <p className="text-indigo-600 dark:text-indigo-400 text-sm mt-1">{round.topic}</p>
                      {round.issue_url && (
                        <div className="mt-1 flex items-center gap-1 flex-wrap">
                          <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                            {round.issue_platform === 'github' ? 'GitHub' : 'GitLab'}
                          </span>
                          {round.issue_number && (
                            <span className="text-xs font-mono text-gray-600 dark:text-gray-400">
                              {round.issue_number}
                            </span>
                          )}
                          <a
                            href={round.issue_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                          >
                            🔗
                          </a>
                        </div>
                      )}
                    </div>
                    {round.average !== null && (
                      <div className="flex gap-1 text-xs">
                        <span className="bg-indigo-100 dark:bg-indigo-900/30 px-2 py-1 rounded text-indigo-600 dark:text-indigo-400 font-semibold">
                          Ø {Number(round.average).toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {round.votes
                      .filter(v => v.vote !== null)
                      .map((vote, vIdx) => (
                        <span key={vIdx} className={`text-xs px-2 py-1 rounded ${
                          vote.name === playerName
                            ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-semibold'
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                        }`}>
                          {vote.vote}
                        </span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  )
}

export default Player
